/*
 * Saved-report view state (2026-10-28, plan 2026-10-28-001 PR 3).
 *
 * A report is the subset of Creative Analysis URL params that describes a
 * view: the tab, the window, the Overview segment, and the explorer's view,
 * rank-by / pivot dimensions, metrics, sort and retargeting setting. Opening
 * a report writes those params (the product stays); saving one reads them.
 * Because the URL already carries the whole view, a pasted link and a saved
 * report reproduce exactly the same thing.
 */

/** URL params a report owns. `seg` is the Overview segment; `rs` is the
 *  explorer's retargeting setting (included / excluded / only). */
export const REPORT_KEYS = ["tab", "w", "seg", "view", "r", "c", "m", "s", "rs"] as const;
export type ReportKey = (typeof REPORT_KEYS)[number];
export type ReportState = Partial<Record<ReportKey, string>>;

/** What each param means when absent (the page's own defaults). */
const DEFAULTS: Record<ReportKey, string> = {
  tab: "overview",
  w: "28",
  seg: "pros",
  view: "chart",
  r: "concept",
  c: "awareness",
  m: "sp,pu,roas",
  s: "sp",
  rs: "all",
};

export function stateFromParams(params: URLSearchParams): ReportState {
  const out: ReportState = {};
  for (const k of REPORT_KEYS) {
    const v = params.get(k);
    if (v !== null && v !== "") out[k] = v;
  }
  return out;
}

/** `params` with the report's view applied: every report param is reset,
 *  then the report's own are set. Everything else (product) is kept. */
export function applyReportState(params: URLSearchParams, state: ReportState): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const k of REPORT_KEYS) next.delete(k);
  for (const k of REPORT_KEYS) {
    const v = state[k];
    if (typeof v === "string" && v !== "" && v !== DEFAULTS[k]) next.set(k, v);
  }
  return next;
}

/** Only the keys a report can carry, as strings (stored state is untrusted
 *  JSON from the server). */
export function cleanState(raw: unknown): ReportState {
  const out: ReportState = {};
  if (raw === null || typeof raw !== "object") return out;
  for (const k of REPORT_KEYS) {
    const v = (raw as Record<string, unknown>)[k];
    if (typeof v === "string" && v.length <= 200) out[k] = v;
  }
  return out;
}

/** Whether two states show the same view, defaults filled in. The explorer
 *  params only count on the Explore tab, the segment only on the Overview. */
export function sameView(a: ReportState, b: ReportState): boolean {
  const full = (s: ReportState) => Object.fromEntries(REPORT_KEYS.map((k) => [k, s[k] ?? DEFAULTS[k]])) as Record<ReportKey, string>;
  const x = full(a);
  const y = full(b);
  if (x.tab !== y.tab || x.w !== y.w) return false;
  if (x.tab === "overview") return x.seg === y.seg;
  const keys: ReportKey[] = ["view", "r", "rs", "m", "s"];
  if (x.view === "grid") keys.push("c");
  return keys.every((k) => x[k] === y[k]);
}

export interface BuiltInReport {
  id: string;
  name: string;
  state: ReportState;
}

/** The Raya-style reports every product gets (Ben, 2026-10-28). Windows a
 *  snapshot doesn't carry fall back to 28 days on the page. */
export const BUILT_IN_REPORTS: readonly BuiltInReport[] = [
  { id: "ov-pros-7", name: "Overview · prospecting · 7 days", state: { tab: "overview", seg: "pros", w: "7" } },
  { id: "ov-ret-7", name: "Overview · retargeting · 7 days", state: { tab: "overview", seg: "ret", w: "7" } },
  { id: "angles-pros", name: "Prospecting angles", state: { tab: "explore", view: "chart", r: "angle", rs: "pros" } },
  { id: "angles-ret", name: "Retargeting angles", state: { tab: "explore", view: "chart", r: "angle", rs: "ret" } },
  { id: "awareness", name: "Awareness breakdown · 14 days", state: { tab: "explore", view: "chart", r: "awareness", rs: "pros", w: "14" } },
  { id: "format", name: "Image vs video vs scroll text", state: { tab: "explore", view: "chart", r: "format", rs: "all" } },
  { id: "creators", name: "Copywriters", state: { tab: "explore", view: "chart", r: "creator", rs: "pros" } },
  { id: "creators-rt", name: "Copywriters · with retargeting", state: { tab: "explore", view: "chart", r: "creator", rs: "all" } },
  { id: "designers", name: "Designers", state: { tab: "explore", view: "chart", r: "designer", rs: "pros" } },
  { id: "families", name: "Concept families", state: { tab: "explore", view: "chart", r: "concept", rs: "pros" } },
];
